import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Users, Target, Zap, Copy, Download, Upload, Plus, Edit2, Trash2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext'; // ✅ Toast notifications
import { fetchWithAuth, postWithAuth, patchWithAuth, deleteWithAuth } from '../utils/api'; // ✅ API wrapper avec auto-refresh

const AIProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [creatingProfile, setCreatingProfile] = useState(false); // ✅ Loading state
  const [formData, setFormData] = useState({
    name: '',
    industry: 'Marketing',
    description: '',
    settings: {}
  });
  const navigate = useNavigate();
  const toast = useToast(); // ✅ Toast notifications

  const industries = [
    'Marketing', 'E-commerce', 'Immobilier', 'Restaurant', 
    'Luxe', 'Startup', 'Influenceur', 'Entreprise', 'Éducation', 'Santé'
  ];

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      // ✅ Utilise fetchWithAuth avec auto-refresh
      const response = await fetchWithAuth('/api/ai-studio/profiles');
      const data = await response.json();
      if (data.success) {
        setProfiles(data.profiles || []);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      // L'erreur 401 est gérée automatiquement par fetchWithAuth
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    
    // If editing, use update endpoint
    if (editingProfile) {
      return handleSaveEdit(e);
    }
    
    setCreatingProfile(true); // ✅ Loading state
    
    try {
      // ✅ Utilise postWithAuth avec auto-refresh
      const response = await postWithAuth('/api/ai-studio/profiles', formData);
      
      // ✅ Gestion d'erreur robuste
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('Profil créé avec succès'); // ✅ Toast succès
        setShowCreateModal(false);
        setFormData({ name: '', industry: 'Marketing', description: '', settings: {} });
        fetchProfiles();
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error(error.message || 'Échec de la création du profil'); // ✅ Toast erreur
    } finally {
      setCreatingProfile(false); // ✅ Stop loading
    }
  };

  const handleDuplicate = async (profileId) => {
    try {
      // ✅ Utilise postWithAuth avec auto-refresh
      const response = await postWithAuth(`/api/ai-studio/profiles/${profileId}/duplicate`, {});
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Échec de la duplication');
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('Profil dupliqué avec succès');
        fetchProfiles();
      }
    } catch (error) {
      console.error('Error duplicating profile:', error);
      toast.error(error.message || 'Échec de la duplication du profil');
    }
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      industry: profile.industry,
      description: profile.description,
      settings: profile.settings || {}
    });
    setShowCreateModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    setCreatingProfile(true); // ✅ Loading state
    
    try {
      // ✅ Utilise fetchWithAuth avec auto-refresh (PUT request)
      const response = await fetchWithAuth(`/api/ai-studio/profiles/${editingProfile.id}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Échec de la modification');
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('Profil modifié avec succès');
        setShowCreateModal(false);
        setEditingProfile(null);
        setFormData({ name: '', industry: 'Marketing', description: '', settings: {} });
        fetchProfiles();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Échec de la mise à jour du profil');
    } finally {
      setCreatingProfile(false);
    }
  };

  const handleDeleteProfile = async (profileId, profileName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le profil "${profileName}" ?`)) {
      return;
    }
    
    try {
      // ✅ Utilise deleteWithAuth avec auto-refresh
      const response = await deleteWithAuth(`/api/ai-studio/profiles/${profileId}`);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Échec de la suppression');
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('Profil supprimé avec succès');
        fetchProfiles();
      } else {
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error(error.message || 'Échec de la suppression du profil');
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingProfile(null);
    setFormData({ name: '', industry: 'Marketing', description: '', settings: {} });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement des profils IA...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Sparkles className="inline mr-3 text-indigo-400" size={36} />
              AI Profiles
            </h1>
            <p className="text-slate-400 text-lg">Créez des profils IA adaptés à votre métier</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all"
            data-testid="create-profile-btn"
          >
            <Plus size={20} className="inline mr-2" />
            Nouveau Profil
          </motion.button>
        </div>
      </div>

      {/* Profiles Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {profiles.map((profile, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200"
            data-testid={`profile-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="text-indigo-400" size={20} />
                  <h3 className="text-xl font-semibold text-white">{profile.name}</h3>
                  {profile.is_template && (
                    <span className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded-full border border-cyan-500/30">
                      Template
                    </span>
                  )}
                </div>
                <p className="text-sm text-indigo-300 mb-2">{profile.industry}</p>
                <p className="text-sm text-slate-400">{profile.description}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDuplicate(profile.id)}
                className="flex-1 px-4 py-2 bg-indigo-500/10 text-indigo-400 rounded-lg border border-indigo-500/30 hover:bg-indigo-500/20 transition-all text-sm"
                data-testid={`duplicate-${index}`}
              >
                <Copy size={16} className="inline mr-2" />
                Dupliquer
              </motion.button>
              {!profile.is_template && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEditProfile(profile)}
                    className="px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                    data-testid={`edit-${index}`}
                  >
                    <Edit2 size={16} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteProfile(profile.id, profile.name)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
                    data-testid={`delete-${index}`}
                  >
                    <Trash2 size={16} />
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleModalClose}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingProfile ? 'Modifier le Profil IA' : 'Créer un Profil IA'}
            </h2>
            <form onSubmit={handleCreateProfile}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Nom du profil</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    required
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Industrie</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500"
                    data-testid="profile-industry-select"
                  >
                    {industries.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-indigo-500 h-24"
                    required
                    data-testid="profile-description-input"
                  />
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creatingProfile}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="submit-profile-btn"
                >
                  {creatingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingProfile ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AIProfiles;